import React from "react";
import {
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  WarningAmberRoundedIcon,
  ShieldOutlinedIcon,
  EmailOutlinedIcon,
  ArrowBackRoundedIcon,
  LockOutlinedIcon,
} from "./utilities/icons";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "./components/header/Header";
import { getUserFromToken } from "./utilities/authUtils";

const ADMIN_EMAIL = "info@aapmor.com";
const BLUE = "#418BFF";
const BLUE_OVERLAY = "rgba(65, 139, 255, 0.12)";

const StatCard = ({ title, description }) => (
  <Box
    sx={{
      borderRadius: "12px",
      border: "1px solid rgba(255,255,255,0.16)",
      background: "rgba(255,255,255,0.08)",
      backdropFilter: "blur(8px)",
      p: 2.5,
      minHeight: 116,
    }}
  >
    <Typography
      sx={{
        color: "#fff",
        fontFamily: "var(--ax-font-heading)",
        fontSize: "1.5rem",
        fontWeight: 500,
        letterSpacing: "-0.02em",
        mb: 0.5,
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        color: "rgba(255,255,255,0.82)",
        lineHeight: 1.55,
        fontSize: "0.82rem",
      }}
    >
      {description}
    </Typography>
  </Box>
);

const DetailCard = ({ icon, title, description }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.25,
      borderRadius: "12px",
      border: "1px solid var(--ax-border)",
      background: "var(--ax-surface)",
    }}
  >
    <Stack direction="row" spacing={1.75} alignItems="flex-start">
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          color: BLUE,
          bgcolor: BLUE_OVERLAY,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          sx={{
            fontWeight: 600,
            color: "var(--ax-ink)",
            mb: 0.5,
            fontSize: "0.92rem",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: "var(--ax-ink-2)",
            lineHeight: 1.6,
            fontSize: "0.84rem",
          }}
        >
          {description}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

function NotAuthorised() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUserFromToken();

  const currentPage = `${window.location.origin}${location.pathname}${location.search}`;
  const employeeName =
    user?.FirstName || user?.name || user?.Email?.split("@")[0] || "";
  const employeeEmail = user?.Email || "";

  const subject = "Aapmor Access Request - Unauthorized Screen";
  const body = [
    "Hello Admin,",
    "",
    "I am unable to access a page in Aapmor SSO and received an unauthorized message.",
    "",
    "Please grant or review access for the following:",
    `Name: ${employeeName}`,
    `Email: ${employeeEmail}`,
    "Employee ID: ",
    "Department / Team: ",
    "Application / Module: ",
    `Page / URL: ${currentPage}`,
    "Business justification: ",
    "",
    "Thank you.",
  ].join("\n");
  const mailtoHref = `mailto:${ADMIN_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "var(--ax-bg)",
      }}
    >
      <Header />

      <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            maxWidth: 1140,
            mx: "auto",
            overflow: "hidden",
            borderRadius: "20px",
            background: "var(--ax-surface)",
            border: "1px solid var(--ax-border)",
            boxShadow: "var(--ax-shadow)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            }}
          >
            <Box
              sx={{
                p: { xs: 3, md: 5 },
                color: "#fff",
                background: BLUE,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "-30%",
                  right: "-20%",
                  width: "80%",
                  height: "80%",
                  background:
                    "radial-gradient(circle, rgba(255,255,255,0.14) 0%, transparent 60%)",
                  pointerEvents: "none",
                }}
              />
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Chip
                  icon={
                    <WarningAmberRoundedIcon
                      sx={{ color: "#fff !important", fontSize: 16 }}
                    />
                  }
                  label="Permission check failed"
                  sx={{
                    color: "rgba(255,255,255,0.95)",
                    bgcolor: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    height: 32,
                    "& .MuiChip-label": {
                      px: 1.25,
                      fontWeight: 500,
                      fontSize: "0.76rem",
                    },
                  }}
                />

                <Typography
                  component="h1"
                  sx={{
                    mt: 3.5,
                    mb: 1.5,
                    maxWidth: 520,
                    fontFamily: "var(--ax-font-heading)",
                    fontSize: { xs: "1.8rem", md: "2.7rem" },
                    fontWeight: 500,
                    lineHeight: 1.05,
                    letterSpacing: "-0.025em",
                  }}
                >
                  Access stopped before entering this workspace.
                </Typography>

                <Typography
                  sx={{
                    maxWidth: 480,
                    color: "rgba(255,255,255,0.86)",
                    fontSize: "0.95rem",
                    lineHeight: 1.65,
                  }}
                >
                  Your sign-in was successful, but the role assigned to your
                  account does not include permission to open this screen. If
                  this looks unexpected, you can raise an access request directly
                  from here.
                </Typography>

                <Box
                  sx={{
                    mt: 4,
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 1.5,
                  }}
                >
                  <StatCard
                    title="403"
                    description="Access denied response from the authorization layer."
                  />
                  <StatCard
                    title="Role"
                    description="Module visible only to users with approved access mapping."
                  />
                  <StatCard
                    title="1 step"
                    description="Send a request to your admin with your employee details."
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                p: { xs: 3, md: 5 },
                background: "var(--ax-surface)",
              }}
            >
              <Chip
                label="Error 403"
                sx={{
                  bgcolor: BLUE_OVERLAY,
                  color: BLUE,
                  border: 0,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  borderRadius: "6px",
                  fontSize: "0.7rem",
                  height: 26,
                }}
              />

              <Typography
                sx={{
                  mt: 2.5,
                  mb: 1.5,
                  fontFamily: "var(--ax-font-heading)",
                  fontSize: { xs: "1.4rem", md: "1.65rem" },
                  fontWeight: 500,
                  letterSpacing: "-0.025em",
                  color: "var(--ax-ink)",
                  lineHeight: 1.15,
                }}
              >
                You do not have permission to view this page.
              </Typography>

              <Typography
                sx={{
                  color: "var(--ax-ink-2)",
                  lineHeight: 1.7,
                  mb: 3,
                  fontSize: "0.92rem",
                }}
              >
                This usually means your role, project mapping, or department
                access has not been assigned for the module you tried to open.
                Use the email button below to request access from your admin.
              </Typography>

              <Stack spacing={1.5}>
                <DetailCard
                  icon={<LockOutlinedIcon fontSize="small" />}
                  title="Why you are seeing this"
                  description="Your account is authenticated, but the destination module requires an access policy that is not currently mapped to your user."
                />
                <DetailCard
                  icon={<ShieldOutlinedIcon fontSize="small" />}
                  title="What to include in your request"
                  description="Share your employee ID, team name, application name, and the page you were trying to access so admin can approve faster."
                />
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  mt: 2.5,
                  p: 2.5,
                  borderRadius: "12px",
                  border: "1px solid var(--ax-border)",
                  background: "var(--ax-bg-secondary)",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "var(--ax-ink)",
                    fontSize: "0.92rem",
                  }}
                >
                  Email your administrator
                </Typography>
                <Typography
                  sx={{
                    color: "var(--ax-ink-2)",
                    fontSize: "0.82rem",
                    mt: 0.5,
                    mb: 1.75,
                  }}
                >
                  Launches your default mail app with a prefilled access
                  request.
                </Typography>

                <Link
                  href={`mailto:${ADMIN_EMAIL}`}
                  underline="none"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "8px",
                    bgcolor: "var(--ax-surface)",
                    border: "1px solid var(--ax-border)",
                    color: BLUE,
                    fontWeight: 500,
                    fontSize: "0.84rem",
                    wordBreak: "break-word",
                    mb: 2,
                  }}
                >
                  <EmailOutlinedIcon fontSize="small" />
                  {ADMIN_EMAIL}
                </Link>

                <Stack
                  component="ul"
                  spacing={1}
                  sx={{
                    pl: 0,
                    my: 0,
                    mb: 2.25,
                    listStyle: "none",
                  }}
                >
                  {[
                    "Subject line is prefilled with an access request tag.",
                    "Message body includes your current page link and user details.",
                    "You can replace the admin mailbox with your support email.",
                  ].map((item) => (
                    <Stack
                      key={item}
                      component="li"
                      direction="row"
                      spacing={1.25}
                      alignItems="flex-start"
                    >
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          bgcolor: BLUE,
                          mt: "9px",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          color: "var(--ax-ink-2)",
                          lineHeight: 1.55,
                          fontSize: "0.84rem",
                        }}
                      >
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                  <Button
                    component="a"
                    href={mailtoHref}
                    variant="contained"
                    startIcon={<EmailOutlinedIcon fontSize="small" />}
                  >
                    Send Access Request
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackRoundedIcon fontSize="small" />}
                    onClick={handleGoBack}
                  >
                    Go Back
                  </Button>
                </Stack>
              </Paper>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ mt: 2.5 }}
              >
                <Chip
                  label="Status: Unauthorized"
                  size="small"
                  sx={{
                    width: "fit-content",
                    bgcolor: "var(--ax-surface)",
                    border: "1px solid var(--ax-border)",
                    color: "var(--ax-ink-2)",
                    fontSize: "0.74rem",
                    fontWeight: 500,
                  }}
                />
                <Chip
                  label={`Reference: ${location.pathname || "/unknown"}`}
                  size="small"
                  sx={{
                    width: "fit-content",
                    bgcolor: "var(--ax-surface)",
                    border: "1px solid var(--ax-border)",
                    color: "var(--ax-ink-2)",
                    fontSize: "0.74rem",
                    fontWeight: 500,
                  }}
                />
              </Stack>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

export default NotAuthorised;
